import React, { Suspense } from 'react';
import { FeatureGate } from './FeatureGate';
import { getFeatureKeyForPath } from '../config/featureFlagRouteConfig';

/**
 * Loading fallback component for route-level lazy loading
 * NOTE: With transparent wrapper pattern, this is only used when explicitly
 * passed as loadingComponent prop. By default, pages show their own skeletons.
 */
const ContentLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-pulse bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-8 max-w-md w-full">
      <div className="space-y-4">
        <div className="h-8 bg-white/10 rounded w-3/4"></div>
        <div className="h-4 bg-white/10 rounded w-full"></div>
        <div className="h-4 bg-white/10 rounded w-5/6"></div>
        <div className="h-4 bg-white/10 rounded w-4/6"></div>
      </div>
    </div>
  </div>
);

interface FeatureGateRouteProps {
  /**
   * Feature key - if provided, uses this directly.
   * If not provided, looks up feature key from path.
   */
  featureKey?: string;
  
  /**
   * Route path - used to lookup feature key if featureKey not provided
   */
  path?: string;
  
  /**
   * Child component to render if access granted
   */
  children: React.ReactNode;
  
  /**
   * Show teaser page on access denial (default: true)
   */
  showTeaser?: boolean;
  
  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode;
  
  /**
   * Query keys to invalidate when feature becomes disabled
   * Useful for cleaning up cached data when user loses access
   */
  queryKeysToInvalidate?: Array<string | unknown[]>;
  
  /**
   * Custom fallback for access denied (instead of teaser page)
   */
  fallback?: React.ReactNode;
  
  /**
   * Additional CSS classes for the wrapper
   */
  className?: string;
}

/**
 * Wrapper for route-level feature gating.
 * Simplifies migration from ProtectedRoute to FeatureGate.
 * 
 * This component:
 * - Automatically looks up feature key from route path if not provided
 * - Integrates with TeaserPage for upgrade prompts
 * - Supports Suspense for lazy-loaded routes
 * - Provides consistent loading states
 * - Handles query cache invalidation on access loss
 * 
 * @example
 * // Explicit feature key
 * <FeatureGateRoute featureKey="portfolio_optimization">
 *   <PortfolioOptimizationPage />
 * </FeatureGateRoute>
 * 
 * @example
 * // Auto-detect from path
 * <FeatureGateRoute path="/portfolio-optimization">
 *   <PortfolioOptimizationPage />
 * </FeatureGateRoute>
 * 
 * @example
 * // With query cache cleanup
 * <FeatureGateRoute 
 *   featureKey="portfolio_optimization"
 *   queryKeysToInvalidate={[
 *     'strategyPnlSummary',
 *     'strategyPnlDaily',
 *     'botRules'
 *   ]}
 * >
 *   <PortfolioOptimizationPage />
 * </FeatureGateRoute>
 */
export const FeatureGateRoute: React.FC<FeatureGateRouteProps> = ({
  featureKey: providedFeatureKey,
  path,
  children,
  showTeaser = true,
  loadingComponent,
  queryKeysToInvalidate = [],
  fallback,
  className
}) => {
  // Determine feature key
  const featureKey = providedFeatureKey || (path ? getFeatureKeyForPath(path) : undefined);
  
  // If no feature key found, show error
  if (!featureKey) {
    console.error('[FeatureGateRoute] No feature key provided or found for path:', path);
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 max-w-md">
          <h2 className="text-red-400 font-semibold text-xl mb-2">Configuration Error</h2>
          <p className="text-red-300 text-sm mb-4">
            No feature flag mapping found for this route.
          </p>
          <p className="text-white/60 text-xs">
            Route: <code className="text-white/80">{path || 'unknown'}</code>
          </p>
          <p className="text-white/60 text-xs mt-2">
            Please contact support if this issue persists.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <FeatureGate
      featureKey={featureKey}
      showTeaser={showTeaser}
      loadingComponent={loadingComponent} // ✅ Don't pass default - let FeatureGate use transparent wrapper
      queryKeysToInvalidate={queryKeysToInvalidate}
      fallback={fallback}
      className={className}
    >
      <Suspense fallback={loadingComponent || <ContentLoadingFallback />}>
        {children}
      </Suspense>
    </FeatureGate>
  );
};

/**
 * Higher-order component version for wrapping route components
 * 
 * @example
 * const ProtectedPortfolioOptimization = withFeatureGateRoute({
 *   featureKey: 'portfolio_optimization',
 *   queryKeysToInvalidate: ['strategyPnlSummary']
 * })(PortfolioOptimizationPage);
 */
export const withFeatureGateRoute = (options: Omit<FeatureGateRouteProps, 'children'>) => {
  return function <P extends object>(Component: React.ComponentType<P>) {
    return function FeatureGatedRouteComponent(props: P) {
      return (
        <FeatureGateRoute {...options}>
          <Component {...props} />
        </FeatureGateRoute>
      );
    };
  };
};

/**
 * Pre-configured route gates for common routes
 * These provide convenient wrappers with the correct feature keys
 */
export const PortfolioOptimizationRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGateRoute 
    featureKey="portfolio_optimization"
    queryKeysToInvalidate={['strategyPnlSummary', 'strategyPnlDaily', 'botRules']}
  >
    {children}
  </FeatureGateRoute>
);

export const PortfolioCommandRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGateRoute 
    featureKey="portfolio_command_center"
    queryKeysToInvalidate={['portfolioSnapshot', 'portfolioMetrics']}
  >
    {children}
  </FeatureGateRoute>
);

export const StrategyDetailsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGateRoute 
    featureKey="strategy_details"
    queryKeysToInvalidate={['strategyDetails', 'strategyMetrics']}
  >
    {children}
  </FeatureGateRoute>
);

export const ForecastRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGateRoute 
    featureKey="portfolio_forecast"
    queryKeysToInvalidate={['forecastData', 'forecastMetrics']}
  >
    {children}
  </FeatureGateRoute>
);

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGateRoute 
    featureKey="admin_panel"
    showTeaser={false}
  >
    {children}
  </FeatureGateRoute>
);

export default FeatureGateRoute;
